import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom'; //added 'Link'
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { useContext } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import watermarkImage from '../assets/coverzone-watermark.png';
import logoImage from '../assets/FullLogo_1.png';
import topLogoImage from '../assets/brokerlogo.png';
import logoOrg1 from '../assets/broker3.jpg';
import logoOrg2 from '../assets/broker4.jpg';
import Header from '../Header';
import Sidebar from '../Sidebar';

const API_URL = process.env.REACT_APP_API_URL;

// Map organizations to logos
const logoMap: { [key: string]: string } = {
  'Example Org': logoOrg1,
  'Org2': logoOrg2,
  // Add more mappings
};

interface Dashboardform {
  id: number;
  name?: string;
  email?: string;
  organization?: string;
  id_no?: string | null;
  mobile_no?: string | null;
  address?: string | null;
  personal?: boolean;
  commercial?: boolean;
  medical?: boolean;
  surname?: string;
}

const Dashboard = () => {
  const [user, setUser] = useState<{ id: number; email: string; username?: string; organization?: string; surname?: string } | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); //added for navigation slider
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/auth/me', {
  headers: { Authorization: `Bearer ${token}` },
});
        console.log('API Response:', response.data); // Debug log to verify organizationName
        setUser(response.data);
      } catch (error) {
        console.error('Error fetching user:', error);
        localStorage.removeItem('token');
        navigate('/login');
      }
    };
    fetchUser();
  }, [navigate]);

  const handleLogout = () => {
    authContext?.setUser(null);
    localStorage.removeItem('token');
    navigate('/login');
  };

  //added for navigation slider
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.error('Failed to load watermark image:', e);
  };

  const handleImageLoad = (type: string) => {
    console.log(`${type} image loaded successfully`);
  };

  if (!user) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex relative">

      {/* Watermark */}
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-0">
        <img
          src={watermarkImage}
          alt="Watermark"
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-100 h-100 opacity-5 z-0 select-none"
          onError={handleImageError}
          onLoad={() => handleImageLoad('Watermark')}
        />
      </div>

      {/* Sidebar */}
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />

      {/* Main Content */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
  {/* Header */}
        <Header
          title="Welcome to the Dashboard Page"
          user={user}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
        />

  <div className="flex-grow flex items-center justify-center">
    <div className="flex flex-col gap-y-4">
      {/* Header Row */}
      <div className="grid grid-cols-[160px_repeat(4,192px)] gap-x-4 md:grid flex-col">
        <div className="w-40 mb-4 md:mb-0"></div>
        <div className="w-48 text-center">
          <span className="text-xl font-bold text-gray-800 whitespace-nowrap inline-block">Open</span>
        </div>
        <div className="w-48 text-center">
          <span className="text-xl font-bold text-gray-800 whitespace-nowrap inline-block">Completed</span>
        </div>
        <div className="w-48 text-center">
          <span className="text-xl font-bold text-gray-800 whitespace-nowrap inline-block">Open For Brokerage</span>
        </div>
        <div className="w-48 text-center">
          <span className="text-xl font-bold text-gray-800 whitespace-nowrap inline-block">Completed For Brokerage</span>
        </div>
      </div>
      {/* Instructions Row */}
      <div className="grid grid-cols-[160px_repeat(4,192px)] gap-x-4 md:grid flex-col">
        <div className="w-40 mb-4 md:mb-0">
          <Link to="/instructions" className="text-xl font-semibold text-gray-800 hover:underline">
            Instructions
          </Link>
        </div>
        <div className="w-48 text-center">
          <Link to="/instructions" className="text-gray-700 text-lg hover:underline">
            10
          </Link>
        </div>
        <div className="w-48 text-center">
          <p className="text-gray-700 text-lg">30</p>
        </div>
        <div className="w-48 text-center">
          <p className="text-gray-700 text-lg">50</p>
        </div>
        <div className="w-48 text-center">
          <p className="text-gray-700 text-lg">70</p>
        </div>
      </div>
      {/* Renewals Row */}
      <div className="grid grid-cols-[160px_repeat(4,192px)] gap-x-4 md:grid flex-col">
        <div className="w-40 mb-4 md:mb-0">
          <span className="text-xl font-semibold text-gray-800">Renewals</span>
        </div>
        <div className="w-48 text-center">
          <p className="text-gray-700 text-lg">20</p>
        </div>
        <div className="w-48 text-center">
          <p className="text-gray-700 text-lg">40</p>
        </div>
        <div className="w-48 text-center">
          <p className="text-gray-700 text-lg">60</p>
        </div>
        <div className="w-48 text-center">
          <p className="text-gray-700 text-lg">80</p>
        </div>
      </div>
      {/* Claims Row */}
      <div className="grid grid-cols-[160px_repeat(4,192px)] gap-x-4 md:grid flex-col">
        <div className="w-40 mb-4 md:mb-0">
          <span className="text-xl font-semibold text-gray-800">Claims</span>
        </div>
        <div className="w-48 text-center">
          <p className="text-gray-700 text-lg">20</p>
        </div>
        <div className="w-48 text-center">
          <p className="text-gray-700 text-lg">40</p>
        </div>
        <div className="w-48 text-center">
          <p className="text-gray-700 text-lg">60</p>
        </div>
        <div className="w-48 text-center">
          <p className="text-gray-700 text-lg">80</p>
        </div>
      </div>
      {/* Quote Row */}
      <div className="grid grid-cols-[160px_repeat(4,192px)] gap-x-4 md:grid flex-col">
        <div className="w-40 mb-4 md:mb-0">
          <span className="text-xl font-semibold text-gray-800">Quote</span>
        </div>
        <div className="w-48 text-center">
          <p className="text-gray-700 text-lg">20</p>
        </div>
        <div className="w-48 text-center">
          <p className="text-gray-700 text-lg">40</p>
        </div>
        <div className="w-48 text-center">
          <p className="text-gray-700 text-lg">60</p>
        </div>
        <div className="w-48 text-center">
          <p className="text-gray-700 text-lg">80</p>
        </div>
      </div>
      {/* Return Debits Row */}
      <div className="grid grid-cols-[160px_repeat(4,192px)] gap-x-4 md:grid flex-col">
        <div className="w-40 mb-4 md:mb-0">
          <span className="text-xl font-semibold text-gray-800">Return Debits</span>
        </div>
        <div className="w-48 text-center">
          <p className="text-gray-700 text-lg">20</p>
        </div>
        <div className="w-48 text-center">
          <p className="text-gray-700 text-lg">40</p>
        </div>
        <div className="w-48 text-center">
          <p className="text-gray-700 text-lg">60</p>
        </div>
        <div className="w-48 text-center">
          <p className="text-gray-700 text-lg">80</p>
        </div>
      </div>
    </div>
  </div>
</div>
    </div>
  );
};

export default Dashboard;